import React, { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { validateEmail } from "@repo/shopify/utils/regex";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import useFormErrorHandler from "@hooks/useFormErrorHandler";

interface IFormData {
  email: string;
  password?: string;
}

interface IProps {
  submit: ({ email, password }: { email: string; password: string }) => Promise<void>;
  onClickFindPassword: () => void;
  onClickRegister: () => void;
}

export default function Login({ submit, onClickFindPassword, onClickRegister }: IProps) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: { email: "", password: "" },
    reValidateMode: "onSubmit",
  });

  const { onCommonError, displayError } = useFormErrorHandler({ values: getValues(), errors });

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit: SubmitHandler<IFormData> = async (formData) => {
    setIsLoading(true);
    await submit({ email: formData.email, password: formData.password || "" }).then(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      <span className="text-title">Email Login</span>
      <form className="flex flex-col gap-2.5" id="login" onSubmit={handleSubmit(onSubmit, onCommonError)}>
        <Input
          {...displayError("email")}
          {...register("email", {
            required: { value: true, message: t("necessary_email") },
            validate: (value) => {
              if (!validateEmail(value)) return t("login.invalid_email");
            },
            onChange: () => {
              clearErrors("email");
            },
          })}
          id="email"
          label="Email Address"
          placeholder="Email Address"
          type="email"
        />
        <Input
          {...displayError("password")}
          {...register("password", {
            required: { value: true, message: t("necessary_password") },
            minLength: { value: 5, message: t("login.min_password") },
            onChange: () => {
              clearErrors("password");
            },
          })}
          id="password"
          label="Password"
          placeholder="Password"
          type="password"
        />
        <div className="pc:gap-1 flex flex-col gap-2.5">
          <Button as="square" className="w-full" loading={isLoading} name="login" type="submit">
            Login
          </Button>
          <Button as="square" className="w-full" name="register" onClick={onClickRegister} variant="secondary">
            Create Account
          </Button>
        </div>
        <Button as="text" className="text-ko" onClick={onClickFindPassword}>
          Forgot Password?
        </Button>
      </form>
    </>
  );
}
